// src/api.ts
import axios from "axios";
import { Project } from "types";

// Export a stable backend origin constant so other modules can import it.
// If `REACT_APP_API_URL` is set (origin without `/api`) use that; otherwise
// fall back to `window.location.origin` when available. If neither is
// available (e.g., SSR build), use an empty string and fall back to
// relative URLs below.
export const backendOrigin =
  (process.env.REACT_APP_API_URL &&
    // Remove a trailing `/api` if present, then strip any trailing slash
    process.env.REACT_APP_API_URL.replace(/\/api\/?$/, "").replace(
      /\/$/,
      ""
    )) ||
  (typeof window !== "undefined" ? window.location.origin : "");

const apiBase = backendOrigin ? `${backendOrigin}/api` : "/api";
const webBase = backendOrigin ? `${backendOrigin}/` : "/";

// Use the computed bases for axios instances.
const api = axios.create({
  baseURL: apiBase,
  withCredentials: true,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
    "X-Requested-With": "XMLHttpRequest",
  },
});

export const webApi = axios.create({
  baseURL: webBase,
  withCredentials: true,
  headers: {
    "X-Requested-With": "XMLHttpRequest",
  },
});

function getCookie(name: string) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(";").shift();
}

webApi.interceptors.request.use((config) => {
  const xsrfToken = getCookie("XSRF-TOKEN");
  if (xsrfToken) {
    config.headers["X-XSRF-TOKEN"] = decodeURIComponent(xsrfToken);
  }
  return config;
});

// Keep api interceptor too so API calls include the XSRF header when available
api.interceptors.request.use((config) => {
  const xsrfToken = getCookie("XSRF-TOKEN");
  if (xsrfToken) {
    config.headers["X-XSRF-TOKEN"] = decodeURIComponent(xsrfToken);
  }
  return config;
});

export const getCsrfToken = async () => {
  try {
    // Always call the backend origin (without `/api`) for sanctum CSRF cookie.
    const url = backendOrigin
      ? `${backendOrigin.replace(/\/$/, "")}/sanctum/csrf-cookie`
      : "/sanctum/csrf-cookie";
    await axios.get(url, { withCredentials: true });
  } catch (error) {
    console.error("Error fetching CSRF token:", error);
  }
};

export const getAnnouncements = (params?: any) =>
  api.get("/announcements", { params });

export const getEvents = (params?: any) => api.get("/events", { params });

export const getProjects = (params?: any) => api.get("/projects", { params });

export const getBusinesses = (params?: any) =>
  api.get("/businesses", { params });

export const getBusiness = (id: string | number) =>
  api.get(`/businesses/${id}`);

export const getProject = (id: number) => api.get<Project>(`/projects/${id}`);

export const createContactInquiry = (data: any) => api.post("/contact", data);

export const getStats = () => api.get("/stats");

export default api;
